import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDropzone } from "@/components/FileTools";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import { Plus, Home, Search, ShieldCheck } from "lucide-react";
import { PROPERTY_TYPES } from "@/lib/propertyTypes";

/**
 * Modulo unificato agente + privato.
 * - Agente/Admin: toggle vendita/ricerca → crea property (se vendita) o mandate search (se ricerca) e chiude.
 * - Buyer/Owner privato: come sopra + step OTP che firma il mandato di intermediazione con Aaron & Partners S.r.l.
 *   contestualmente nella stessa modale.
 */
export default function UnifiedListingDialog({ onCreated }) {
  const { user, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("role"); // role | form | otp
  const [chosenRole, setChosenRole] = useState(null); // agent | owner | buyer
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("sale"); // sale | search
  const [otp, setOtp] = useState("");
  const [pendingMandate, setPendingMandate] = useState(null); // {name,email,area,budget,ref}

  // form
  const [f, setF] = useState({
    title: "", description: "", location: "", city: "", country: "", price: "",
    bedrooms: "", bathrooms: "", area_sqm: "", property_type: "villa",
    // search
    search_area: "", search_budget: "",
    // signer (privato)
    signer_name: user?.name || "", signer_email: user?.email || "", signer_fiscal_code: "",
  });
  const [images, setImages] = useState([]);
  const [mandateFile, setMandateFile] = useState(null); // PDF mandato firmato (agente, vendita)

  const isPrivate = chosenRole === "owner" || chosenRole === "buyer";
  const inCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";

  const resetAll = () => {
    setStep("role"); setChosenRole(null); setKind("sale"); setOtp(""); setPendingMandate(null); setImages([]); setMandateFile(null);
    setF({
      title: "", description: "", location: "", city: "", country: "", price: "",
      bedrooms: "", bathrooms: "", area_sqm: "", property_type: "villa",
      search_area: "", search_budget: "",
      signer_name: user?.name || "", signer_email: user?.email || "", signer_fiscal_code: "",
    });
  };

  // Create the property (if sale) or the search mandate (if search),
  // then — for private users — issue OTP so they sign the Aaron & Partners intermediation contextually.
  const submitForm = async () => {
    setBusy(true);
    try {
      let refDescription = "";
      if (kind === "sale") {
        const body = {
          title: f.title, description: f.description, location: f.location,
          city: f.city, country: f.country,
          price: parseFloat(f.price) || 0,
          bedrooms: parseInt(f.bedrooms) || 0,
          bathrooms: parseInt(f.bathrooms) || 0,
          area_sqm: parseFloat(f.area_sqm) || 0,
          commission_pct: 3,
          property_type: f.property_type,
        };
        const { data } = await api.post("/properties", body);
        const prop = data.property || data;
        for (const img of images) {
          const fd = new FormData();
          fd.append("file_id", img.file_id);
          await api.post(`/properties/${prop.id}/images`, fd);
        }
        if (mandateFile && !isPrivate) {
          const fd = new FormData();
          fd.append("file_id", mandateFile.file_id);
          await api.post(`/properties/${prop.id}/mandate`, fd);
        }
        refDescription = `${f.title} · ${f.city}, ${f.country}`;
      } else {
        refDescription = `${f.search_area || "—"} · budget €${f.search_budget || "—"}`;
      }

      if (!isPrivate) {
        // Agente: se ricerca, crea direttamente il mandato senza OTP (usa NDNCA firmato in fase di adesione)
        if (kind === "search") {
          await api.post("/mandates", {
            mandate_type: "search",
            signer_name: user.name, signer_email: user.email,
            search_area: f.search_area,
            search_budget: parseFloat(f.search_budget) || 0,
            property_type: f.property_type,
            accepted: true, otp_code: null,
            attached_file_id: mandateFile?.file_id || null,
          });
        }
        toast.success(kind === "sale" ? "Inserzione creata" : "Ricerca creata");
        setOpen(false); resetAll(); onCreated?.();
        return;
      }

      // Privato: richiedi OTP per firmare il mandato di intermediazione contestualmente
      await api.post("/otp/request", { purpose: "mandate", signer_email: f.signer_email });
      setPendingMandate({
        name: f.signer_name, email: f.signer_email, fc: f.signer_fiscal_code,
        area: f.search_area, budget: f.search_budget, ref: refDescription,
      });
      setStep("otp");
      toast.success("Codice OTP inviato via email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const submitOtp = async () => {
    if (!pendingMandate || !otp) return;
    setBusy(true);
    try {
      await api.post("/mandates", {
        mandate_type: kind === "sale" ? "sale" : "search",
        signer_name: pendingMandate.name,
        signer_email: pendingMandate.email,
        signer_fiscal_code: pendingMandate.fc || null,
        property_ref: kind === "sale" ? pendingMandate.ref : null,
        property_type: f.property_type,
        search_area: kind === "search" ? pendingMandate.area : null,
        search_budget: kind === "search" ? (parseFloat(pendingMandate.budget) || 0) : null,
        exclusivity_months: 6,
        commission_terms: "Aaron & Partners S.r.l. · 20% sulle commissioni chiuse via piattaforma",
        accepted: true,
        otp_code: otp.trim(),
      });
      toast.success("Mandato firmato e inserzione pubblicata");
      setOpen(false); resetAll(); onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP non valido");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetAll(); }}>
      <DialogTrigger asChild>
        <Button data-testid="open-unified-dialog" className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
          <Plus className="h-4 w-4 me-2" /> {t("new_listing_search", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#D8CFBB] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="unified-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {step === "otp" ? t("sign_mandate_title", lang) : t("new_listing_search", lang)}
          </DialogTitle>
        </DialogHeader>

        {step === "role" && (
          <>
            <p className="text-sm text-[#6B5F4C]">Prima di procedere, dicci in che veste stai caricando l'inserzione o la ricerca.</p>
            <div className="grid gap-3">
              <button
                type="button"
                data-testid="role-agent"
                onClick={() => { setChosenRole("agent"); setStep("form"); }}
                className="border border-[#D8CFBB] rounded-lg p-5 text-start hover:border-[#B8965A] hover:bg-[#FAFAF8] transition-colors"
              >
                <p className="text-xs tracking-[0.3em] uppercase text-[#B8965A] mb-1">Agente</p>
                <p className="text-sm text-[#1A1610]">Sono un agente immobiliare con mandato firmato dal cliente. Carico foto e allego il PDF del mandato.</p>
              </button>
              <button
                type="button"
                data-testid="role-owner"
                onClick={() => {
                  setChosenRole("owner");
                  setOpen(false);
                  window.dispatchEvent(new CustomEvent("open-mandate-form", { detail: { kind: "sale" } }));
                }}
                className="border border-[#D8CFBB] rounded-lg p-5 text-start hover:border-[#B8965A] hover:bg-[#FAFAF8] transition-colors"
              >
                <p className="text-xs tracking-[0.3em] uppercase text-[#B8965A] mb-1">Proprietario</p>
                <p className="text-sm text-[#1A1610]">Vendo o loco un immobile mio. Firmo il mandato con Aaron &amp; Partners S.r.l. via OTP (commissioni da concordare).</p>
              </button>
              <button
                type="button"
                data-testid="role-buyer"
                onClick={() => {
                  setChosenRole("buyer");
                  setOpen(false);
                  window.dispatchEvent(new CustomEvent("open-mandate-form", { detail: { kind: "search" } }));
                }}
                className="border border-[#D8CFBB] rounded-lg p-5 text-start hover:border-[#B8965A] hover:bg-[#FAFAF8] transition-colors"
              >
                <p className="text-xs tracking-[0.3em] uppercase text-[#B8965A] mb-1">Buyer</p>
                <p className="text-sm text-[#1A1610]">Cerco un immobile da acquistare. Firmo il mandato di ricerca con Aaron &amp; Partners S.r.l. via OTP (commissioni da concordare).</p>
              </button>
            </div>
          </>
        )}

        {step === "form" && (
          <>
            {/* Toggle vendita/ricerca */}
            <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#EDE4CF] border border-[#D8CFBB]">
              <button
                type="button" onClick={() => setKind("sale")}
                data-testid="kind-sale"
                className={`h-10 rounded-full text-sm tracking-wider transition-colors ${kind === "sale" ? "bg-[#B8965A] text-white" : "text-[#4A3F30]"}`}
              >
                <Home className="inline h-4 w-4 me-1" /> Vendita immobile
              </button>
              <button
                type="button" onClick={() => setKind("search")}
                data-testid="kind-search"
                className={`h-10 rounded-full text-sm tracking-wider transition-colors ${kind === "search" ? "bg-[#B8965A] text-white" : "text-[#4A3F30]"}`}
              >
                <Search className="inline h-4 w-4 me-1" /> Ricerca acquisto
              </button>
            </div>

            {kind === "sale" && (
              <>
                <div>
                  <Label className="text-[#4A4A4A]">Tipo di immobile</Label>
                  <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
                    <SelectTrigger data-testid="uf-type" className="bg-[#F5F0E6] border-[#D8CFBB]">
                      <SelectValue placeholder="Seleziona il tipo…" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((pt) => (
                        <SelectItem key={pt.value} value={pt.value} data-testid={`uf-type-opt-${pt.value}`}>
                          {pt[lang] || pt.it}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#4A4A4A]">Titolo</Label>
                  <Input data-testid="uf-title" className={inCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[#4A4A4A]">Descrizione</Label>
                  <Textarea data-testid="uf-desc" className={inCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[#4A4A4A]">Città</Label><Input data-testid="uf-city" className={inCls} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
                  <div><Label className="text-[#4A4A4A]">Paese</Label><Input data-testid="uf-country" className={inCls} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
                </div>
                <div><Label className="text-[#4A4A4A]">Indirizzo/zona</Label><Input data-testid="uf-loc" className={inCls} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><Label className="text-[#4A4A4A]">Prezzo €</Label><Input data-testid="uf-price" type="number" className={inCls} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
                  <div><Label className="text-[#4A4A4A]">Camere</Label><Input data-testid="uf-bed" type="number" className={inCls} value={f.bedrooms} onChange={(e) => setF({ ...f, bedrooms: e.target.value })} /></div>
                  <div><Label className="text-[#4A4A4A]">Bagni</Label><Input data-testid="uf-bath" type="number" className={inCls} value={f.bathrooms} onChange={(e) => setF({ ...f, bathrooms: e.target.value })} /></div>
                  <div><Label className="text-[#4A4A4A]">m²</Label><Input data-testid="uf-area" type="number" className={inCls} value={f.area_sqm} onChange={(e) => setF({ ...f, area_sqm: e.target.value })} /></div>
                </div>
                <div>
                  <Label className="text-[#4A4A4A]">Foto</Label>
                  <FileDropzone testId="uf-photo" kind="image" accept="image/*" label={t("add_photo", lang)}
                    onUploaded={(u) => setImages((p) => [...p, u])} />
                  {images.length > 0 && <p className="text-xs text-[#6B5F4C] mt-1">{images.length} foto caricate</p>}
                </div>
                {!isPrivate && (
                  <div>
                    <Label className="text-[#4A4A4A]">Mandato firmato (PDF)</Label>
                    <FileDropzone testId="uf-mandate" kind="pdf" accept="application/pdf" label="Carica il PDF del mandato di vendita firmato"
                      onUploaded={(u) => setMandateFile(u)} />
                    {mandateFile && <p className="text-xs text-emerald-700 mt-1">Mandato caricato ✓</p>}
                  </div>
                )}
              </>
            )}

            {kind === "search" && (
              <>
                <div>
                  <Label className="text-[#4A4A4A]">Tipo di immobile cercato</Label>
                  <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
                    <SelectTrigger data-testid="uf-search-type" className="bg-[#F5F0E6] border-[#D8CFBB]">
                      <SelectValue placeholder="Seleziona il tipo…" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((pt) => (
                        <SelectItem key={pt.value} value={pt.value} data-testid={`uf-search-type-opt-${pt.value}`}>
                          {pt[lang] || pt.it}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[#4A4A4A]">Zona di ricerca</Label><Input data-testid="uf-search-area" className={inCls} value={f.search_area} onChange={(e) => setF({ ...f, search_area: e.target.value })} /></div>
                <div><Label className="text-[#4A4A4A]">Budget €</Label><Input data-testid="uf-search-budget" type="number" className={inCls} value={f.search_budget} onChange={(e) => setF({ ...f, search_budget: e.target.value })} /></div>
                <div><Label className="text-[#4A4A4A]">Descrizione</Label><Textarea data-testid="uf-search-notes" className={inCls} placeholder="Tipologia cercata, caratteristiche, esigenze specifiche..." value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
                {!isPrivate && (
                  <div>
                    <Label className="text-[#4A4A4A]">Mandato di ricerca firmato (PDF)</Label>
                    <FileDropzone testId="uf-search-mandate" kind="pdf" accept="application/pdf" label="Carica il PDF del mandato di ricerca firmato"
                      onUploaded={(u) => setMandateFile(u)} />
                    {mandateFile && <p className="text-xs text-emerald-700 mt-1">Mandato caricato ✓</p>}
                  </div>
                )}
              </>
            )}

            {isPrivate && (
              <div className="mt-3 border border-[#B8965A]/40 bg-[#B8965A]/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#B8965A]" />
                  <p className="text-xs tracking-[0.25em] uppercase text-[#B8965A]">Mandato Aaron &amp; Partners S.r.l.</p>
                </div>
                <p className="text-xs text-[#6B5F4C]">
                  Contestualmente alla pubblicazione firmerai via OTP email il mandato di intermediazione con Aaron &amp; Partners S.r.l. (Via Pescaglia 16, 00146 Roma). Commissione piattaforma: 20% sulle chiusure.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[#4A4A4A]">Nome firmatario</Label><Input data-testid="uf-signer-name" className={inCls} value={f.signer_name} onChange={(e) => setF({ ...f, signer_name: e.target.value })} /></div>
                  <div><Label className="text-[#4A4A4A]">Email firmatario</Label><Input data-testid="uf-signer-email" type="email" className={inCls} value={f.signer_email} onChange={(e) => setF({ ...f, signer_email: e.target.value })} /></div>
                </div>
                <div><Label className="text-[#4A4A4A]">Codice fiscale (opzionale)</Label><Input data-testid="uf-signer-fc" className={inCls} value={f.signer_fiscal_code} onChange={(e) => setF({ ...f, signer_fiscal_code: e.target.value })} /></div>
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel", lang)}</Button>
              <Button onClick={submitForm} disabled={busy} data-testid="uf-submit"
                className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
                {busy ? "…" : (isPrivate ? "Firma e pubblica" : "Pubblica")}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "otp" && (
          <>
            <p className="text-sm text-[#6B5F4C]">
              Ti abbiamo inviato un codice OTP a <b className="font-mono">{pendingMandate?.email}</b>. Inseriscilo per firmare il mandato di intermediazione con Aaron &amp; Partners S.r.l. e pubblicare l'inserzione.
            </p>
            <Input
              data-testid="uf-otp"
              inputMode="numeric" maxLength={6}
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="text-center text-2xl tracking-[0.5em] font-mono bg-[#F5F0E6] border-[#D8CFBB]"
              placeholder="000000"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("form")}>Indietro</Button>
              <Button onClick={submitOtp} disabled={busy || otp.length !== 6} data-testid="uf-otp-submit"
                className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
                {busy ? "…" : "Firma e pubblica"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
